import { Box } from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { FirstStep } from "../components/EvaluationForm/FirstStep/FirstStep";
import { FormExpired } from "../components/EvaluationForm/FormExpired/FormExpired";
import { FormNotFound } from "../components/EvaluationForm/FormNotFound/FormNotFound";
import { FormSucceededContainer } from "../components/EvaluationForm/FormSucceeded/FormSucceededContainer";
import { Step } from "../components/EvaluationForm/Step/Step";
import Loading from "../components/Loading";
import { FormsStepper } from "../components/UI/FormsStepper/FormsStepper";
import Header from "../components/UI/Menus/Header/Header";
import useFetch from "../hooks/useFetch";
import { IFormSatisfaction } from "../models/form-satisfaction";
import {
  evalMovTicketQuestions,
  evaluationFormQuestions,
} from "../services/evaluationForm.service";
import { EvaluationFormProvider } from "../store/contexts/EvaluationFormContext";

const EvaluationFormPage = () => {
  const { id, type } = useParams();

  const [form, SetForm] = useState<IFormSatisfaction>();

  const [formStarted, setFormStarted] = useState(false);

  const [initialStep, setInitialStep] = useState<number | undefined>(undefined);

  const [viewAnswers, setViewAnswers] = useState(false);

  const startFormHandler = useCallback(() => {
    setFormStarted(true);
  }, []);

  const goToStartHandler = useCallback(() => {
    setFormStarted(true);
    setInitialStep(0);
    setViewAnswers(true);
  }, []);

  const resetInitialStep = useCallback(() => {
    setInitialStep(undefined);
  }, []);

  const allCurrentQuestionsAnswered = useMemo(() => {
    if (!form) return false;

    const formQuestions = form.evaluationForm.evaluationFormQuestions || [];
    const allAnswers = [
      ...(form.evalMovAnswerEncSatisfactions || []),
      ...(form.evalMovAnswerTickets || []),
    ];

    return (
      formQuestions.length > 0 &&
      formQuestions.every((q) =>
        allAnswers.some((asw) => asw.questionId === q.question.id),
      )
    );
  }, [form]);

  const isFormReadOnly = (!!form?.answerDate && allCurrentQuestionsAnswered) || viewAnswers;

  const { sendRequest, loading } = useFetch(evaluationFormQuestions);
  const { sendRequest: sendRequestTicketQuestions, loading: eLoading } =
    useFetch(evalMovTicketQuestions);

  const fetchEvaluationForm = useCallback(async () => {
    if (!id) return;
    const { data, success } = await sendRequest(id);
    if (data && success) {
      SetForm(data);
    }
  }, [sendRequest, id]);

  const fetchEvalMovTicketQuestions = useCallback(async () => {
    if (!id) return;
    const { data, success } = await sendRequestTicketQuestions(id);
    if (data && success) {
      SetForm(data);
    }
  }, [sendRequestTicketQuestions, id]);

  useEffect(() => {
    if (type) {
      fetchEvalMovTicketQuestions();
    } else {
      fetchEvaluationForm();
    }
  }, [fetchEvaluationForm, fetchEvalMovTicketQuestions, type]);

  const renderFormControl = useCallback(() => {
    if (!form) return;

    if (!form.answerDate && form.isResponseExpired) return;

    if (form.evaluationForm.answeredForm?.code === "2") {
      setFormStarted(true);
    }

    const formQuestions = form?.evaluationForm.evaluationFormQuestions || [];
    const allAnswers = [
      ...(form?.evalMovAnswerEncSatisfactions || []),
      ...(form?.evalMovAnswerTickets || []),
    ];

    if (!viewAnswers) {
      for (let i = 0; i < formQuestions.length; i++) {
        const question = form?.evaluationForm.evaluationFormQuestions[i];

        if (form?.evaluationForm.evaluationFormQuestions) {
          if (form?.answerDate && allCurrentQuestionsAnswered) {
            setFormStarted(true);
            setInitialStep(formQuestions.length + 2);
            break;
          }

          if (allAnswers.length === 0) {
            setInitialStep(0);
            break;
          }

          if (
            !allAnswers.find((asw) => asw.questionId === question?.question.id)
          ) {
            setInitialStep(i);
            setFormStarted(true);
            break;
          } else {
            setInitialStep(0);
          }
        }
      }
    }
  }, [form, viewAnswers, allCurrentQuestionsAnswered]);

  useLayoutEffect(() => {
    renderFormControl();
  }, [renderFormControl]);

  useEffect(() => {
    return () => {
      setViewAnswers(false);
    };
  }, []);

  if (loading || eLoading)
    return (
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loading />
      </Box>
    );

  return (
    <EvaluationFormProvider
      initialAnswers={
        form?.evalMovAnswerEncSatisfactions ?? form?.evalMovAnswerTickets ?? []
      }
    >
      <Box sx={{ width: "100%", height: "100vh" }}>
        <Header alwaysShowLogo accessPortalBtn />
        {!form && <FormNotFound />}
        {form && !form.answerDate && form.isResponseExpired && <FormExpired />}
        {form &&
          !form.answerDate &&
          !form.isResponseExpired &&
          !formStarted && (
            <FirstStep
              evaluationForm={form.evaluationForm}
              onStartForm={startFormHandler}
            />
          )}
        {form &&
          (form.answerDate || !form.isResponseExpired) &&
          formStarted && (
            <FormsStepper
              layoutForm={form.evaluationForm.layoutForm}
              endComponent={
                <FormSucceededContainer
                  evaluationForm={form.evaluationForm}
                  onGoToStart={goToStartHandler}
                  onResetInitialStep={resetInitialStep}
                />
              }
              questions={form.evaluationForm.evaluationFormQuestions}
              hasAnswerDate={isFormReadOnly}
              initialStep={initialStep}
              currentActiveStep={initialStep}
              steps={[
                ...form.evaluationForm.evaluationFormQuestions.map(
                  (stepForm, index) => ({
                    component: (
                      <Step
                        question={stepForm.question}
                        stepsLength={
                          form.evaluationForm.evaluationFormQuestions.length + 1
                        }
                        hasAnswerDate={isFormReadOnly}
                      />
                    ),
                    step: index,
                  }),
                ),
              ]}
            />
          )}
      </Box>
    </EvaluationFormProvider>
  );
};

export default EvaluationFormPage;
